import React, { useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import '../../assets/css/editor.scss';
import { ElearningParamType } from '@/pages/Module/routes';
import PreviewRoutes from './routes';

import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetFramesQuery,
  useGetHelperContentQuery,
  useGetHelperFaqQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
  useGetResourcesQuery,
  useGetVideoLinkQuery,
} from '@/modules/services';
import { useErrorHandler } from 'react-error-boundary';
import PlayerUi from '@/components/Player';
import {
  getCurrentFrame,
  hasVocamData,
  processQuestion,
  useIsMobileScreenSize,
} from '@/lib/util';
import { QuizDataType } from '@/modules/types/quiz';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import { AnswerDataType } from '@/modules/types/question';
import { Spinner } from 'elmo-elements';

const Preview = (): React.ReactElement => {
  const history = useHistory();

  const handleError = useErrorHandler();
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const queryParams = new URLSearchParams(window.location.search);
  const isQuizEnabled = queryParams.get('enableQuiz');
  const goToAssessment =
    queryParams.get('goToAssessment')?.toLocaleLowerCase() === 'true';
  const previewMobile = queryParams.get('mobile') === 'true';

  const [isQuizReady, setIsQuizReady] = useState<boolean>(
    isQuizEnabled?.toLocaleLowerCase() === 'true',
  );
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsIsLoading,
  } = useGetElearningBehaviourSettingsQuery();

  const elearning = useGetElearningQuery(elearningId);
  const frames = useGetFramesQuery({ elearningId });
  const quiz = useGetQuizQuery(elearningId);
  const questions = useGetQuestionsQuery(
    {
      quizId: `${quiz.data?.id}`,
    },
    {
      skip: !quiz.data?.id || !quiz.data,
    },
  );
  const helperContent = useGetHelperContentQuery('');
  const helperFaq = useGetHelperFaqQuery('');
  const resources = useGetResourcesQuery({ elearningId });
  const [currentFrameId, setCurrentFrameId] = useState(frameId || '');

  const currentFrame = useMemo(
    () => getCurrentFrame(frames.data ?? [], currentFrameId ?? ''),
    [frames.data, currentFrameId],
  );
  const { data: vocamVideoLink } = useGetVideoLinkQuery(currentFrameId ?? '', {
    skip: !currentFrameId || !hasVocamData(currentFrame),
  });

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  const { isMobile } = useIsMobileScreenSize();

  useEffect(() => {
    if (frames.data) {
      const firstFrameId =
        !currentFrameId && !goToAssessment
          ? frames.data[0].id
          : frames.data[frames.data.length - 1].id;

      if (!currentFrameId || goToAssessment) {
        history.replace(
          generatePath(PreviewRoutes.path, {
            elearningId,
            frameId: firstFrameId,
          }),
        );
        setCurrentFrameId(firstFrameId);
      }
    }
  }, [elearningId, currentFrameId, frames, history]);

  if (
    elearning.isError ||
    helperContent.isError ||
    helperFaq.isError ||
    resources.isError ||
    quiz.isError ||
    questions.isError
  ) {
    handleError(elearning.error);
  }

  if (
    elearning.isLoading ||
    frames.isLoading ||
    helperContent.isLoading ||
    helperFaq.isLoading ||
    resources.isLoading ||
    quiz.isLoading ||
    questions.isLoading ||
    elearningBehaviourSettingsIsLoading ||
    !quiz.data ||
    !questions.data
  ) {
    return <Spinner />;
  }

  const playerHelper = {
    content: helperContent.data,
    faq: helperFaq.data,
  };

  const quizData = {
    ...quiz.data,
    questions: questions.data,
  };

  const playerHandlerState = {
    questionsToAsk: quiz.data.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: quizData.questions,
    questionsCompleted: [],
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      callback();
    },
    submitQuestion: (
      answer: AnswerDataType,
      callback: (payload: any) => void,
    ) => {
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions = quizData.questions
        ? quizData.questions
        : [];
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (
      playerQuizData: any,
      callback: (payload: any) => void,
    ) => {
      const divisor = quiz.data?.quizMetadata.questionNo || 1;
      const score = Math.round(
        (playerHandlerState.correctAnswers / divisor) * 100,
      );

      callback({ score });
    },
    startAssessment: () => {
      setIsQuizReady(true);
    },
    moveToFrame: (nextFrameId: string) => {
      setCurrentFrameId(nextFrameId);
      const nextPath = generatePath(PreviewRoutes.path, {
        elearningId,
        frameId: nextFrameId,
      });
      history.push(nextPath);
    },
  };

  return (
    <PlayerUi
      isPreview={true}
      isPlayable={true}
      currentFrameId={currentFrameId}
      isQuizEnabled={isQuizReady}
      elearning={elearning.data}
      frames={frames.data}
      quiz={quizData as QuizDataType}
      resources={resources.data || []}
      helperContent={playerHelper.content || []}
      helperFaq={playerHelper.faq || []}
      handlers={playerHandlers}
      isMobileView={previewMobile || isMobile}
      vocamVideoLink={vocamVideoLink}
      elearningBehaviourSettings={elearningBehaviourSettingsData}
    />
  );
};

export default Preview;
